import { type Page, success, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import type { SteelAction } from "./index";

/**
 * 抖音创作者平台登录 RPA
 *
 * 流程：
 * 1. 打开抖音创作者平台，注入反屏蔽脚本
 * 2. Bypass 页面事件屏蔽（通过 blank 页跳转清除脚本监听）
 * 3. 点击登录按钮，等待二维码弹窗
 * 4. 监听页面跳转到 /creator-micro/home 判断登录成功
 * 5. 从响应中提取 msToken 和用户信息
 * 6. 轮询获取 Security SDK localStorage 数据
 * 7. 获取浏览器指纹信息（screen、navigator等）
 * 8. 收集 cookie 并返回结果
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());

    let page: Page;
    let isSelfClosing = false;
    let handleBrowserDisconnected: (() => void) | undefined;
    const cleanup = async () => {
        if (handleBrowserDisconnected) {
            task._steelBrowser?.off("disconnected", handleBrowserDisconnected);
        }
        if (page && !page.isClosed()) {
            await page.close().catch(() => {});
        }
    };

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            url: "https://creator.douyin.com/",
        });

        if (!task._session?.id) throw new Error("Session ID 不存在");

        handleBrowserDisconnected = () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        };
        task._steelBrowser?.on("disconnected", handleBrowserDisconnected);
    } catch (e) {
        task.logger.error("浏览器启动失败", e);
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // 拦截不必要的资源，提升加载速度
    await page.route("**/*.mp4", (route) => route.abort());
    await page.route("**/*.ttf", (route) => route.abort());

    // 自定义样式：隐藏扫码登录，调整输入框布局
    const customStyle = `
        #douyin_login_comp_flat_panel [class*="scan_qrcode_login"] {
            display: none !important;
        }
        [class*="douyin_login_landing_page"] #douyin_login_comp_flat_panel [class*="flat_container"] {
            align-items: center !important;
            justify-content: center !important;
        }
        [class*="douyin_login_landing_page"] #douyin_login_comp_flat_panel [class*="flat_container"] [class*="input_login"] {
            margin-left: 0 !important;
        }
        [class*="douyin_login_landing_page"] #douyin_login_comp_flat_panel [class*="flat_container"] [class*="input_login"] [class*="tab_box"] [class*="line"] {
            display: none !important;
        }
        [class*="douyin_login_landing_page"] #douyin_login_comp_flat_panel [class*="flat_container"] [class*="input_login"] [class*="tab_box"] span:nth-child(3) {
            display: none !important;
        }
    `;
    // 注入自定义样式：隐藏扫码登录，调整输入框布局
    try {
        await page.addStyleTag({ content: customStyle });
    } catch (_e) {
        task.logger.warn("样式注入失败");
    }
    // 注入持久化脚本：禁用 requestAnimationFrame + 移除页面事件屏蔽
    await page.addInitScript(() => {
        // 抖音特定：移除事件屏蔽
        if (window.location.hostname === "creator.douyin.com") {
            window.requestAnimationFrame = () => 0;
        }

        /**
         * 移除页面事件屏蔽层
         * 抖音创作者平台会通过 pointer-events: none 阻止用户交互，
         * 需要持续检测并恢复
         */
        const removeEventBlockers = () => {
            try {
                // 移除所有 pointer-events: none 的元素屏蔽
                document.querySelectorAll('[style*="pointer-events: none"]').forEach((el) => {
                    (el as HTMLElement).style.pointerEvents = "auto";
                });
                document.body.style.pointerEvents = "auto";
            } catch (_) {
                // 静默失败
            }
        };

        removeEventBlockers();

        // 通过 MutationObserver 监听 DOM 变化，实时移除屏蔽
        if (typeof MutationObserver !== "undefined") {
            const observer = new MutationObserver(removeEventBlockers);
            observer.observe(document.documentElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["style"],
            });
        }

        // 兜底：定时检测（MutationObserver 可能遗漏部分场景）
        setInterval(removeEventBlockers, 3000);
    });

    // ========== 第二步：Bypass 页面事件屏蔽 ==========
    task.logger.info("页面创建成功，开始 Bypass 页面事件屏蔽...");
    try {
        // 等待关键 JS 加载完成（该脚本包含事件屏蔽逻辑）
        await page.waitForResponse(
            (response) => response.url().includes("/272.6591d0af.js") && response.status() === 200,
            { timeout: 15 * 1000 },
        );
        await page.waitForLoadState("networkidle");
        // 通过 blank 页跳转再返回，清除页面上已绑定的事件屏蔽监听器
        await page.goto("about:blank");
        await page.goBack({ waitUntil: "domcontentloaded" });
        task.logger.info("已返回登录页面，页面事件屏蔽 Bypass 完成");
    } catch (e) {
        task.logger.warn("Bypass 超时，继续执行...", { error: e });
    }

    // 等待页面稳定
    await page.waitForTimeout(2000);

    // ========== 通过 blank 页跳转再返回，再次注入自定义样式：隐藏扫码登录，调整输入框布局 ==========
    try {
        await page.addStyleTag({ content: customStyle });
    } catch (_e) {
        task.logger.warn("样式注入失败");
    }

    // ========== 第三步：点击登录按钮 ==========
    try {
        const loginButtonSelector = ".douyin-creator-master-icon-default.douyin-creator-master-icon-user_circle";

        // 等待登录按钮可见
        await page.waitForSelector(loginButtonSelector, { state: "visible", timeout: 10000 });

        // 优先使用 JS 点击（绕过可能残留的事件屏蔽）
        const clicked = await page.evaluate((selector) => {
            const btn = document.querySelector(selector) as HTMLElement;
            if (btn) {
                btn.click();
                return true;
            }
            return false;
        }, loginButtonSelector);

        if (clicked) {
            task.logger.info("登录按钮点击成功（JS 方式）");
        } else {
            // 回退到 Playwright force 点击
            await page.locator(loginButtonSelector).click({ force: true, timeout: 5000 });
            task.logger.info("登录按钮点击成功（Playwright force 方式）");
        }
    } catch (e) {
        task.logger.error("点击登录按钮失败", e);
        isSelfClosing = true;
        await cleanup();
        return { code: 500, message: "无法点击登录按钮，页面可能已被屏蔽", data: {} };
    }

    // 等待登录弹窗/二维码出现
    // await page.waitForTimeout(3000);

    // ========== 第四步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages[0].id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();

    task.reportService?.settingSessionID?.(task._session?.id || "");

    task.logger.info(`远程调试地址已生成，现在可以安全访问远程页面${connectAddress}`);
    await reporter(0, "浏览器启动成功，请使用抖音App扫码登录……", { connectAddress });

    // ========== 第五步：监听登录状态与用户信息 ==========
    const userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            /** 清理所有监听器 */
            const cleanup = (error?: Error) => {
                if (finished) return;
                finished = true;
                page.off("framenavigated", handlePageNavigation);
                page.off("response", handleResponse);
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            };

            /**
             * 监听页面跳转到首页
             * 所有登录方式（扫码/手机号等）最终都会跳转到 /creator-micro/home
             */
            const handlePageNavigation = async (frame: any) => {
                try {
                    if (frame === page.mainFrame() && frame.url().includes("/creator-micro/home")) {
                        task.logger.info("页面已跳转到首页，登录成功");
                        await reporter(0, "登录成功，正在获取用户信息...", { stage: "scanQrcode", connectAddress });
                        cleanup();
                    }
                } catch (err) {
                    task.logger.error("handlePageNavigation 异常:", err);
                }
            };

            /**
             * 监听响应，提取 msToken 和用户信息
             *
             * - x-ms-token / x-tt-timestamp 响应头 → 构造 msToken cookie
             * - /aweme/v1/creator/user/info → 用户昵称、头像
             * - /aweme/v1/creator/pc/user/info/ → 用户 uniqueId
             */
            const handleResponse = async (resp: Response) => {
                try {
                    const url = resp.url();
                    const headers = resp.headers();

                    // 从响应头提取 msToken 并写入 cookie
                    const xmst = headers["x-ms-token"];
                    const date = headers["x-tt-timestamp"];

                    if (xmst && date) {
                        // msToken 有效期 7 天
                        const mstExpirDate = new Date(Number(date) * 1000 + 7 * 24 * 60 * 60 * 1000);
                        const msToken = {
                            name: "msToken",
                            value: xmst,
                            domain: ".bytedance.com",
                            expires: mstExpirDate.getTime() / 1000,
                            httpOnly: true,
                            path: "/",
                            sameSite: "None" as const,
                            secure: true,
                        };
                        await page.context().addCookies([msToken]);
                        task.debug &&
                            task.logger.info("msToken 添加成功", {
                                token: xmst.substring(0, 20) + "...",
                                timestamp: date,
                            });
                    }

                    // 获取用户昵称和头像
                    if (url.includes("aweme/v1/creator/user/info")) {
                        try {
                            const jsonUserInfo: { user_profile: { avatar_url: string; nick_name: string } } =
                                await resp.json();
                            userInfo.name = jsonUserInfo.user_profile.nick_name ?? "";
                            userInfo.avatar = jsonUserInfo.user_profile.avatar_url ?? "";
                            task.debug && task.logger.info("用户信息获取成功", userInfo);
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", { url, error: e });
                        }
                    }
                    // 获取用户 uniqueId
                    else if (url.includes("/aweme/v1/creator/pc/user/info/")) {
                        try {
                            const jsonUserInfo: { uid: string } = await resp.json();
                            userInfo.uniqueId = jsonUserInfo.uid ?? "";
                            task.debug && task.logger.info("用户 uniqueId 获取成功", userInfo);
                        } catch (e) {
                            task.logger.warn("无法解析用户ID:", { url, error: e });
                        }
                    }
                } catch (err) {
                    task.logger.error("handleResponse 异常:", err);
                }
            };

            // 注册监听器
            page.on("framenavigated", handlePageNavigation);
            page.on("response", handleResponse);
        });
    } catch (e) {
        isSelfClosing = true;
        const errorMessage = e instanceof Error ? e.message : String(e);
        await cleanup();
        return { code: 500, message: errorMessage, data: { stage: "scanQrcode", connectAddress } };
    }

    // ========== 第六步：提取 Security SDK localStorage 数据 ==========
    let securityData: Record<string, string> = {};
    const maxRetries = 15;

    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
        securityData = await page.evaluate(() => {
            const data: Record<string, string> = {};
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    data[key] = localStorage.getItem(key) || "";
                }
            }
            return data;
        });

        if (
            securityData["security-sdk/s_sdk_pri_key"] &&
            securityData["security-sdk/s_sdk_pub_key"] &&
            securityData["security-sdk/s_sdk_sign_data_key/web_protect"] &&
            securityData["security-sdk/s_sdk_server_cert_key"] &&
            securityData["security-sdk/s_sdk_crypt_sdk"]
        ) {
            task.logger.info("Security SDK 数据已获取");
            break;
        }

        task.logger.info(`等待 Security SDK 数据... (${retryCount + 1}/${maxRetries})`);
        await page.waitForTimeout(1500);
    }

    // ========== 第七步：获取浏览器指纹信息 ==========
    // 【新增】获取屏幕和导航器信息
    const browserInfo = await page.evaluate(() => {
        const userAgentData = (navigator as any).userAgentData;

        return {
            screen_width: window.screen.width.toString(),
            screen_height: window.screen.height.toString(),
            browser_language: navigator.language,
            browser_platform: navigator.platform,
            browser_name: navigator.appCodeName,
            "sec-ch-ua-platform": userAgentData.platform,
            "sec-ch-ua": userAgentData?.brands
                ?.map((item: { brand: string; version: string }) => `"${item.brand}";v="${item.version}"`)
                .join(", "),
            userAgent: navigator.userAgent,
        };
    });

    task.logger.info(`浏览器指纹信息已获取: ${JSON.stringify(browserInfo)}`);

    // ========== 第八步：收集 cookie 并返回结果 ==========
    await page.waitForTimeout(3000);
    const cookie = JSON.stringify(await task._steelBrowserContext?.cookies());
    isSelfClosing = true;
    await cleanup();

    task.logger.info(
        `登录流程完成，正在返回结果: ${JSON.stringify({ connectAddress, cookie, userInfo, securityData, browserInfo })}`,
    );

    return success(
        {
            connectAddress,
            cookie,
            userInfo,
            extraParam: {
                ...securityData,
                browserInfo,
            },
        },
        "登录成功！",
    );
};
